//Caution: Don't change anything in this component without proper KT

import React, { useState, useRef, useEffect } from "react";
import Popper, { PopperPlacementType } from "@mui/material/Popper";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Fade from "@mui/material/Fade";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import { PickersShortcutsItem } from "@mui/x-date-pickers/PickersShortcuts";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { useSearchParams } from "react-router-dom";
import { setIsPaginationOrSearchClicked } from "../../features/dataStoreReqSlice";
import { useDispatch } from "react-redux";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import RenderLabelWithTooltip from "../../utils/RenderLabelTooltipInFilter";
interface FilterProps {
  key: string;
  name?: string;
  label: string;
  searchTerm?: any; // Optional now
  showSearch?: boolean;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedOption: any;
  filterName: string;
  data?: any[];
  onSelectionChange: (updatedSelection: any) => void;
  count: number;
  colorSelected: string;
  multiSelect: boolean;
  valueMapping?: any;
  isDateRangeFilter?: boolean;
  onDateRangeChange?: (dateRange: [Dayjs | null, Dayjs | null]) => void;
  siteNames?: any;
  trafficSources?: any;
  filterRoot?: any;
  onSelectionApplied?: (selection: any) => void;
  options?: {
    label: string;
    value: string;
  }[];
}

interface FilterComponentProps {
  filterConfig: FilterProps[];
  clearFilters: () => void;
  paginationKey?: string;
}

const FilterComponent: React.FC<FilterComponentProps> = ({
  filterConfig,
  clearFilters,
  paginationKey,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] =
    useState<PopperPlacementType>("bottom-start");
  const [selectedContentIndex, setSelectedContentIndex] = useState<number>(0);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const prevContentIndexRef = useRef<number>(selectedContentIndex);
  const dispatch = useDispatch();

  const [shouldSortSelectedToTop, setShouldSortSelectedToTop] = useState(false);
  const [initialSelectedSet, setInitialSelectedSet] = useState<Set<string>>(
    new Set(),
  );

  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleClick =
    (newPlacement: PopperPlacementType, index: number) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      //-- Cancel any pending reset when switching
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      const config = filterConfig[index];
      const hasSelectedItems =
        Array.isArray(config.selectedOption) &&
        config.selectedOption.length > 0;

      const isSame = selectedContentIndex === index;
      const shouldOpen = !(open && isSame);

      setAnchorEl(event.currentTarget);
      setPlacement(newPlacement);
      setSelectedContentIndex(index);
      setOpen(shouldOpen);

      if (shouldOpen) {
        if (hasSelectedItems) {
          setInitialSelectedSet(new Set(config.selectedOption));
          setShouldSortSelectedToTop(true);
        } else {
          setInitialSelectedSet(new Set());
          setShouldSortSelectedToTop(false);
        }
      }
    };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        setOpen(false);

        // Cancel existing timer before setting a new one
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
        }

        // Delay reset to prevent flickering during popup switch
        closeTimerRef.current = setTimeout(() => {
          setShouldSortSelectedToTop(false);
          setInitialSelectedSet(new Set());

          // Clear search term
          const currentConfig = filterConfig[selectedContentIndex];
          if (currentConfig?.onSearchChange) {
            currentConfig.onSearchChange({
              target: { value: "" },
            } as React.ChangeEvent<HTMLInputElement>);
          }

          closeTimerRef.current = null; // clean up
        }, 200); // small delay
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorEl, filterConfig, selectedContentIndex]);

  useEffect(() => {
    const filtersString = searchParams.get("filters");

    if (filtersString) {
      const filtersObject: Record<string, string[]> = {};
      filtersString.split("&").forEach((filter) => {
        const [key, value] = filter.split("=");
        if (key && value) {
          filtersObject[key] = value
            .split(",")
            .map((v) => decodeURIComponent(v));
        }
      });

      filterConfig.forEach((config) => {
        if (filtersObject[config.key]) {
          config.onSelectionChange(filtersObject[config.key]);
        }
      });
    }
    // eslint-disable-next-line
  }, [searchParams]);
  useEffect(() => {
    if (prevContentIndexRef.current !== selectedContentIndex) {
      if (scrollBoxRef.current) {
        scrollBoxRef.current.scrollTop = 0;
      }
      prevContentIndexRef.current = selectedContentIndex;
    }
  }, [selectedContentIndex]);

  const handleClearFilters = () => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Preserve selectedSite and search
    const selectedSite = newSearchParams.get("selectedSite");

    // Clear only the filters inside "filters and search"
    if (newSearchParams.has("filters") || newSearchParams.get("search")) {
      newSearchParams.delete("filters");
      newSearchParams.delete("search");
    }

    // Restore preserved parameters
    if (selectedSite) {
      newSearchParams.set("selectedSite", selectedSite);
    }

    setSearchParams(newSearchParams);
    clearFilters(); // Reset filter states
  };

  //Use This function to handle date range change
  const handleDateRangeChange = (
    newValue: [Dayjs | null, Dayjs | null],
    key: any,
  ) => {
    const formatted = newValue.map((date) =>
      date ? dayjs(date).format("YYYY-MM-DD") : null,
    );

    // Update the selected option
    filterConfig[selectedContentIndex].onSelectionChange(formatted);

    // Update the searchParams
    const newSearchParams = new URLSearchParams(searchParams);

    // Extract existing filters and convert them to an object
    const filtersObject: Record<any, any> = {};
    const existingFilters = newSearchParams.get("filters");

    if (existingFilters) {
      existingFilters.split("&").forEach((filter) => {
        const [key, value] = filter.split("=");
        if (key && value) {
          filtersObject[key] = value
            .split(",")
            .map((v) => decodeURIComponent(v));
        }
      });
    }

    // Update the date filter key
    if (formatted[0] && formatted[1]) {
      filtersObject[key] = formatted; // Assuming "date" is the key for the date filter
    } else {
      delete filtersObject[key]; // Remove filter if no selection
    }

    // Reconstruct the filters query string
    const newFiltersString = Object.entries(filtersObject)
      .map(
        ([key, values]) =>
          `${key}=${values.map((v: any) => encodeURIComponent(v)).join(",")}`,
      )
      .join("&");

    // Update searchParams
    if (newFiltersString) {
      newSearchParams.set("filters", newFiltersString);
    } else {
      newSearchParams.delete("filters");
    }

    // Reset the `page` query parameter to `0`
    const pageKey = paginationKey || "page"; // Default to "page" if not provided
    newSearchParams.set(pageKey, "0");

    setSearchParams(newSearchParams);
  };

  const handleOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    option: string,
  ) => {
    const { checked } = event.target;
    const currentConfig = filterConfig[selectedContentIndex];

    const selectedOptions = Array.isArray(currentConfig.selectedOption)
      ? currentConfig.selectedOption
      : [];

    let updatedSelection;
    // if (currentConfig.multiSelect) {
    //   updatedSelection = checked
    //     ? [...selectedOptions, option]
    //     : selectedOptions.filter((item) => item !== option);
    // } else {
    //   updatedSelection = checked ? [option] : [];
    // }

    if (currentConfig.multiSelect) {
      if (checked) {
        // Remove if already exists, then add to end
        updatedSelection = [
          ...selectedOptions.filter((item) => item !== option),
          option,
        ];
      } else {
        updatedSelection = selectedOptions.filter((item) => item !== option);
      }
    } else {
      updatedSelection = checked ? [option] : [];
    }

    currentConfig.onSelectionChange(updatedSelection);
    if (currentConfig.onSelectionApplied) {
      currentConfig.onSelectionApplied(updatedSelection);
    }

    // Generate new filter query format
    const newSearchParams = new URLSearchParams(searchParams);
    dispatch(setIsPaginationOrSearchClicked(false));

    // Extract existing filters and convert them to an object
    const filtersObject: Record<string, string[]> = {};
    const existingFilters = newSearchParams.get("filters");

    if (existingFilters) {
      existingFilters.split("&").forEach((filter) => {
        const [key, value] = filter.split("=");
        if (key && value) {
          filtersObject[key] = value
            .split(",")
            .map((v) => decodeURIComponent(v));
        }
      });
    }

    // Update the current filter key
    if (updatedSelection.length > 0) {
      filtersObject[currentConfig.key] = updatedSelection;
    } else {
      delete filtersObject[currentConfig.key]; // Remove filter if no selection
    }

    // Reconstruct the filters query string
    const newFiltersString = Object.entries(filtersObject)
      .map(
        ([key, values]) =>
          `${key}=${values.map((v) => encodeURIComponent(v)).join(",")}`,
      )
      .join("&");

    // Update searchParams
    if (newFiltersString) {
      newSearchParams.set("filters", newFiltersString);
    } else {
      newSearchParams.delete("filters");
    }

    const pageKey = paginationKey || "page";
    newSearchParams.set(pageKey, "0");

    setSearchParams(newSearchParams);
  };

  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
      label: "This Week",
      getValue: () => {
        const today = dayjs();
        return [today.startOf("week"), today.endOf("week")];
      },
    },
    {
      label: "Last Week",
      getValue: () => {
        const today = dayjs();
        const prevWeek = today.subtract(7, "day");
        return [prevWeek.startOf("week"), prevWeek.endOf("week")];
      },
    },
    {
      label: "Last 7 Days",
      getValue: () => {
        const today = dayjs();
        return [today.subtract(7, "day"), today];
      },
    },
    {
      label: "Current Month",
      getValue: () => {
        const today = dayjs();
        return [today.startOf("month"), today.endOf("month")];
      },
    },
    {
      label: "Next Month",
      getValue: () => {
        const today = dayjs();
        const startOfNextMonth = today.endOf("month").add(1, "day");
        return [startOfNextMonth, startOfNextMonth.endOf("month")];
      },
    },
    { label: "Reset", getValue: () => [null, null] },
  ];
  const getFilteredOptions = () => {
    const {
      key,
      searchTerm = "",
      siteNames = [],
      trafficSources = [],
      data,
      filterRoot,
      filterName,
      options,
    } = filterConfig[selectedContentIndex] || {};
    const safeSearch = typeof searchTerm === "string" ? searchTerm : "";
    const searchLower = safeSearch.toLowerCase();
    if (Array.isArray(options)) {
      return options
        .filter((o) => o.label.toLowerCase().includes(searchLower))
        .map((o) => o.value);
    }
    const validData = Array.isArray(data) ? data : [];
    const seen = new Set<any>();
    const results: any[] = [];

    if (key === "asset") {
      for (const site of siteNames) {
        const name = site?.site?.ui_site_name?.toLowerCase();
        if (name?.includes(searchLower)) {
          const val = site.key;
          if (val && !seen.has(val)) {
            seen.add(val);
            results.push(val);
          }
        }
      }
    } else if (key === "source") {
      for (const source of trafficSources) {
        const name = source?.traffic_source_name?.toLowerCase();
        if (name?.includes(searchLower)) {
          const val = source.shared_uid;
          if (val && !seen.has(val)) {
            seen.add(val);
            results.push(val);
          }
        }
      }
    } else {
      for (const item of validData) {
        let nested = item;

        if (Array.isArray(filterRoot)) {
          for (const key of filterRoot) {
            nested = nested?.[key];
            if (!nested) break;
          }
        } else if (typeof filterRoot === "string") {
          nested = nested?.[filterRoot];
        }

        let values: any[] = [];
        if (Array.isArray(nested)) {
          for (const nestedItem of nested) {
            const val = nestedItem?.[filterName];
            if (Array.isArray(val)) values.push(...val);
            else if (val !== null && val !== undefined) values.push(val);
          }
        } else {
          const val = item?.[filterName];
          if (Array.isArray(val)) values = val;
          else if (val !== null && val !== undefined) values = [val];
        }

        for (const val of values) {
          const str = String(val);
          if (
            str !== "null" &&
            str !== "undefined" &&
            str.toLowerCase().includes(searchLower) &&
            !seen.has(str)
          ) {
            seen.add(str);
            results.push(str);
          }
        }
      }
    }
    return results;
  };

  const filteredOptions = getFilteredOptions();

  const displayOption = (option: any) => {
    const { options } = filterConfig[selectedContentIndex] || {};

    if (options) {
      const found = options.find((o) => o.value === option);
      return <RenderLabelWithTooltip label={found?.label ?? option} />;
    }

    const siteMap = new Map(
      (filterConfig[selectedContentIndex]?.siteNames || []).map((site: any) => [
        site.key,
        site.site.ui_site_name,
      ]),
    );
    const sourceMap = new Map(
      (filterConfig[selectedContentIndex]?.trafficSources || []).map(
        (source: any) => [source.shared_uid, source.traffic_source_name],
      ),
    );
    const { key, valueMapping } = filterConfig[selectedContentIndex] || {};

    if (key === "asset") return siteMap.get(option) ?? null;
    if (key === "source") return sourceMap.get(option) ?? null;
    return valueMapping?.[option] ?? option;
  };

  const getListHeight = () => {
    const itemHeight = 40; // Height of each item in pixels
    const minHeight = 80; // Minimum height for the list (2 items)
    const maxHeight = 300; // Maximum height for the list

    // Calculate height based on number of items (with a minimum of 2 items)
    const calculatedHeight = Math.max(
      minHeight,
      Math.min(maxHeight, filteredOptions.length * itemHeight),
    );

    return calculatedHeight;
  };

  // Checkbox item renderer for react-window
  const CheckboxItem = ({
    index,
    style,
    data,
  }: {
    index: number;
    style: React.CSSProperties;
    data: any;
  }) => {
    const option = data.items[index];
    const isSelected = data.selectedSet.has(option);
    const shouldShowDivider = data.shouldSortSelectedToTop;
    const isLastOfSelected =
      shouldShowDivider &&
      data.selectedCount > 0 &&
      data.selectedCount < data.items.length &&
      index === data.selectedCount - 1;

    return (
      <div
        style={{
          ...style,
          borderBottom: isLastOfSelected ? "1px dotted #ccc" : undefined,
          paddingLeft: 8,
          paddingRight: 8,
        }}
      >
        <FormControlLabel
          key={option}
          control={
            <Checkbox
              color="secondary"
              checked={isSelected}
              onChange={(event) => handleOptionChange(event, option)}
            />
          }
          label={displayOption(option)}
          sx={{
            width: "100%",
            margin: 0,
            "& .MuiFormControlLabel-label": {
              fontSize: "14px",
            },
          }}
        />
      </div>
    );
  };

  // Radio item renderer for react-window
  const RadioItem = ({
    index,
    style,
    data,
  }: {
    index: number;
    style: React.CSSProperties;
    data: any;
  }) => {
    const option = data.items[index];
    const config = filterConfig[selectedContentIndex];
    const selectedOption = config.selectedOption?.[0]; // single selected value
    const isSelected = selectedOption === option;

    const shouldShowDivider = data.shouldSortSelectedToTop;
    const selectedCount = data.selectedCount;

    // Show dotted border after the selected item only when sorting is active
    const isLastOfSelected =
      shouldShowDivider &&
      selectedCount > 0 &&
      selectedCount < data.items.length &&
      index === selectedCount - 1;

    return (
      <div
        style={{
          ...style,
          borderBottom: isLastOfSelected ? "1px dotted #ccc" : undefined,
          paddingLeft: 8,
          paddingRight: 8,
        }}
      >
        <FormControlLabel
          key={option}
          value={option}
          control={
            <Radio
              color="secondary"
              checked={isSelected}
              onChange={(event) => handleOptionChange(event, option)}
            />
          }
          label={displayOption(option)}
          style={{ width: "100%" }}
        />
      </div>
    );
  };

  const renderStandardList = (
    sortedFilteredOptions: any,
    selectedVisibleCount: any,
  ) => {
    const config = filterConfig[selectedContentIndex];

    if (config.multiSelect) {
      return (
        <Box>
          {sortedFilteredOptions.map((option: any, index: any) => {
            const isSelected = config.selectedOption?.includes(option);
            const isLastOfSelected =
              shouldSortSelectedToTop &&
              selectedVisibleCount > 0 &&
              selectedVisibleCount < sortedFilteredOptions.length &&
              index === selectedVisibleCount - 1;
            return (
              <div
                key={option}
                style={{
                  borderBottom: isLastOfSelected
                    ? "1px dotted #ccc"
                    : undefined,
                  paddingLeft: 8,
                  paddingRight: 8,
                }}
              >
                <FormControlLabel
                  sx={{ padding: "0px 0px 0px 4px" }}
                  control={
                    <Checkbox
                      color="secondary"
                      checked={isSelected}
                      onChange={(event) => handleOptionChange(event, option)}
                    />
                  }
                  label={displayOption(option)}
                />
              </div>
            );
          })}
        </Box>
      );
    } else {
      return (
        <Box minWidth={320} sx={{ padding: "16px" }}>
          <RadioGroup
            value={config.selectedOption?.[0] || ""}
            onChange={(event) => handleOptionChange(event, event.target.value)}
          >
            {sortedFilteredOptions.map((option: any, index: any) => {
              const isSelected = config.selectedOption?.[0] === option;
              const isLastOfSelected =
                shouldSortSelectedToTop &&
                selectedVisibleCount > 0 &&
                selectedVisibleCount < sortedFilteredOptions.length &&
                index === selectedVisibleCount - 1;

              return (
                <div
                  key={option}
                  style={{
                    borderBottom: isLastOfSelected
                      ? "1px dotted #ccc"
                      : undefined,
                    paddingLeft: 8,
                    paddingRight: 8,
                  }}
                >
                  <FormControlLabel
                    value={option}
                    control={<Radio checked={isSelected} color="secondary" />}
                    label={displayOption(option)}
                  />
                </div>
              );
            })}
          </RadioGroup>
        </Box>
      );
    }
  };

  // Decide whether to use virtualization based on number of options
  const renderOptionsList = () => {
    const config = filterConfig[selectedContentIndex];
    // const selectedArray: string[] = config.selectedOption || [];
    // const selectedSet = new Set(selectedArray);
    // const filteredSet = new Set(filteredOptions);

    const listHeight = getListHeight();

    let sortedFilteredOptions = filteredOptions;

    if (shouldSortSelectedToTop && initialSelectedSet.size > 0) {
      const selectedVisible = filteredOptions.filter((opt) =>
        initialSelectedSet.has(opt),
      );

      sortedFilteredOptions = [
        ...selectedVisible,
        ...filteredOptions.filter((opt) => !initialSelectedSet.has(opt)),
      ];
    }

    const selectedVisibleCount = sortedFilteredOptions.filter((opt) =>
      initialSelectedSet.has(opt),
    ).length;

    if (filteredOptions.length <= 5) {
      return renderStandardList(sortedFilteredOptions, selectedVisibleCount);
    }

    return (
      <Box
        data-tracker="247"
        height={listHeight}
        width="100%"
        sx={{ padding: config.multiSelect ? "0" : "16px" }}
      >
        <AutoSizer disableHeight>
          {({ width }) => (
            <>
              <List
                height={listHeight}
                width={width}
                itemCount={sortedFilteredOptions.length}
                itemData={{
                  items: sortedFilteredOptions,
                  selectedSet: new Set(config.selectedOption),
                  sortedTopSet: initialSelectedSet, // 👈 this is now the snapshot
                  selectedCount: shouldSortSelectedToTop
                    ? selectedVisibleCount
                    : 0,
                  shouldSortSelectedToTop,
                }}
                itemSize={40} // Height of each item
              >
                {config.multiSelect ? CheckboxItem : RadioItem}
              </List>
            </>
          )}
        </AutoSizer>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
      <Popper
        sx={{ zIndex: 1200 }}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        transition
        key={`filter-popper-${selectedContentIndex}`}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper ref={popperRef}>
              <Box
                sx={{
                  padding: "12px 24px 24px 24px",
                  height: "auto",
                  overflowY: "auto",
                  scrollBehavior: "smooth",
                  transition: "all 0.3s ease",
                  scrollbarWidth: "thin",
                  minWidth: 320,
                  maxWidth: "100%",
                  p: 2,
                }}
                maxHeight={550}
                ref={scrollBoxRef}
              >
                {filterConfig[selectedContentIndex].showSearch && (
                  <TextField
                    data-tracker="247"
                    color="secondary"
                    label="Search..."
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    value={filterConfig[selectedContentIndex].searchTerm || ""}
                    onChange={filterConfig[selectedContentIndex].onSearchChange}
                    sx={{ marginBottom: 1 }}
                  />
                )}
                {/* Show "No results found" if search yields no results */}
                {!filterConfig[selectedContentIndex].isDateRangeFilter &&
                filterConfig[selectedContentIndex].showSearch &&
                filteredOptions.length === 0 ? (
                  <Typography
                    sx={{
                      color: "#888",
                      fontSize: "14px",
                      textAlign: "center",
                      padding: "24px 0",
                    }}
                  >
                    No results found
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontWeight: "500",
                      color: "#000000DE",
                      paddingBlock: "16px",
                    }}
                  >
                    {filterConfig[selectedContentIndex].label}
                  </Typography>
                )}
                {filterConfig[selectedContentIndex].isDateRangeFilter ? (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div data-tracker="247">
                      <StaticDateRangePicker
                        value={
                          filterConfig[selectedContentIndex].selectedOption
                            ? [
                                dayjs(
                                  filterConfig[selectedContentIndex]
                                    .selectedOption[0],
                                ),
                                dayjs(
                                  filterConfig[selectedContentIndex]
                                    .selectedOption[1],
                                ),
                              ]
                            : [null, null] // default value for date range
                        }
                        onChange={(newValue) =>
                          handleDateRangeChange(
                            newValue,
                            filterConfig[selectedContentIndex]?.key,
                          )
                        }
                        slotProps={{
                          shortcuts: {
                            items: shortcutsItems,
                          },
                          actionBar: { actions: [] },
                        }}
                        calendars={2}
                      />
                    </div>
                  </LocalizationProvider>
                ) : (
                  renderOptionsList()
                )}
              </Box>
            </Paper>
          </Fade>
        )}
      </Popper>
      {filterConfig.map((config, index) => (
        <Button
          sx={{
            // eslint-disable-next-line
            backgroundColor:
              (open && selectedContentIndex === index) ||
              (filterConfig[selectedContentIndex]?.selectedOption?.length &&
                selectedContentIndex === index)
                ? "#f7eef9"
                : "transparent",
            "&:hover": {
              backgroundColor: "rgba(156, 39, 176, 0.04)",
            },
          }}
          key={config.key}
          onClick={handleClick("bottom-start", index)}
        >
          <span
            style={{
              color: config.count > 0 ? config.colorSelected : "#000000DE",
            }}
          >
            {config.name ? config.name : config.key}
          </span>
          {config.count > 0 ? (
            <span style={{ color: config.colorSelected }}>+{config.count}</span>
          ) : (
            <KeyboardArrowDownIcon style={{ color: "#000000DE" }} />
          )}
        </Button>
      ))}
      <Button
        sx={{
          color: "#000000DE",
          "&:hover": { backgroundColor: "rgba(156, 39, 176, 0.04)" },
        }}
        onClick={handleClearFilters}
      >
        Clear Filter
      </Button>
    </Box>
  );
};

export default FilterComponent;
